import { getFunctions, httpsCallable } from "firebase/functions";

import { firebaseApp } from "./firebase";

export { eventGuestsToCsv, generatePublicCentralEventUrl } from "./central-event-guest-utils";

export type CentralPublicEvent = {
  address: string;
  endAt: string;
  id: string;
  livestreamUrl: string;
  startAt: string;
  title: string;
  type: string;
  venue: string;
};

export type CentralEventGuest = {
  checkedInAt: string | null;
  checkInMethod: string;
  consent: string;
  createdAt: string | null;
  email: string;
  eventId: string;
  id: string;
  name: string;
  note: string;
  partySize: number;
  phone: string;
  relationship: string;
  responseStatus: string;
  source: string;
  updatedAt: string | null;
};

export type CentralEventGuestList = {
  context: {
    caseId: string;
    checkInEnabled: boolean;
    event: CentralPublicEvent;
    lovedOneName: string;
    qrEnabled: boolean;
    rsvpEnabled: boolean;
  };
  counts: {
    checkedIn: number;
    consented: number;
    expected: number;
    registrations: number;
    walkIns: number;
  };
  guests: CentralEventGuest[];
};

export type PublicEventResponseInput = {
  action: "rsvp" | "check_in";
  consent?: "service_updates" | "thank_you" | "marketing" | "";
  email?: string;
  eventId: string;
  eventKey: string;
  funeralHomeId: string;
  name: string;
  note?: string;
  partySize?: number;
  phone?: string;
  relationship?: string;
  responseStatus?: "attending" | "not_attending";
  slug: string;
};

const functions = getFunctions(firebaseApp, "us-east1");

export async function submitPublicCentralEventResponse(input: PublicEventResponseInput) {
  const callable = httpsCallable<
    PublicEventResponseInput,
    { duplicate: boolean; registrationId: string; status: "rsvp_recorded" | "checked_in" }
  >(functions, "submitPublicCentralEventResponse");
  return (await callable(input)).data;
}

export async function listCentralEventGuests(caseId: string, eventId: string) {
  const callable = httpsCallable<{ caseId: string; eventId: string }, CentralEventGuestList>(
    functions,
    "listCentralEventGuests",
  );
  return (await callable({ caseId, eventId })).data;
}

export async function checkInCentralEventGuest(input: {
  caseId: string;
  eventId: string;
  idempotencyKey: string;
  registrationId: string;
}) {
  const callable = httpsCallable<typeof input, { duplicate: boolean; registrationId: string; status: "checked_in" }>(
    functions,
    "checkInCentralEventGuest",
  );
  return (await callable(input)).data;
}

export async function addCentralEventWalkIn(input: {
  caseId: string;
  consent?: "service_updates" | "thank_you" | "marketing" | "";
  email?: string;
  eventId: string;
  eventKey: string;
  name: string;
  phone?: string;
  relationship?: string;
}) {
  const callable = httpsCallable<typeof input, { duplicate: boolean; registrationId: string; status: "checked_in" }>(
    functions,
    "addCentralEventWalkIn",
  );
  return (await callable(input)).data;
}

export function getCentralEventGuestErrorMessage(error: unknown) {
  const code = typeof error === "object" && error && "code" in error ? String(error.code) : "";
  const message = error instanceof Error ? error.message.replace(/^FirebaseError:\s*/i, "") : "";
  if (code.includes("permission-denied") || code.includes("unauthenticated")) {
    return "Your account cannot manage this event guest list.";
  }
  if (code.includes("not-found")) return "This published event is no longer available.";
  if (code.includes("failed-precondition") || code.includes("invalid-argument")) {
    return message || "Review the guest details and try again.";
  }
  return "We could not complete that event guest action. Try again.";
}
